import {
  createSlice,
  isFulfilled,
  isPending,
  isRejected,
} from "@reduxjs/toolkit";
import { ObjectiveDto, objectivesState } from "./objectives.types";
import {
  addKeyResult,
  createCheckIn,
  createGroupObjective,
  createOrganizationObjective,
  createPersonalObjective,
  deleteKeyResult,
  deleteObjective,
  getAreaObjectives,
  getGroupObjectives,
  getObjective,
  getOrganizationObjectives,
  getPersonalObjectives,
  linkProject,
  linkTask,
  unlinkProject,
  unlinkTask,
  updateKeyResult,
  updateObjective,
} from "./objectives.thunk";
import { objectiveProgress } from "./objectives.progress";

const initialState: objectivesState = {
  objectives: {},
  isError: false,
  isSuccess: false,
  isLoading: false,
};

const allThunks = [
  getPersonalObjectives,
  getAreaObjectives,
  getGroupObjectives,
  getOrganizationObjectives,
  createPersonalObjective,
  createGroupObjective,
  createOrganizationObjective,
  getObjective,
  updateObjective,
  deleteObjective,
  addKeyResult,
  updateKeyResult,
  deleteKeyResult,
  createCheckIn,
  linkProject,
  unlinkProject,
  linkTask,
  unlinkTask,
] as const;

/*
 * Which objectives belong to which list. Archived objectives stay in the
 * store after an update, but no list shows them.
 */

const isVisible = (objective: ObjectiveDto) => objective.status !== "ARCHIVED";

// The user's own; the store also holds personal goals others shared
export const isPersonalObjective = (
  objective: ObjectiveDto,
  userId: string | undefined,
) => objective.isPersonal && objective.ownerId === userId;

/*
 * Everything in a group's or organization's area: its own goals, the user's
 * personal goals there and those others shared (what its list returns)
 */
export const isInGroupArea = (objective: ObjectiveDto, groupId: string) =>
  objective.groupId === groupId;

export const isInOrganizationArea = (
  objective: ObjectiveDto,
  organizationId: string,
) => objective.groupId === null && objective.organizationId === organizationId;

export const isGroupObjective = (objective: ObjectiveDto, groupId: string) =>
  !objective.isPersonal && isInGroupArea(objective, groupId);

export const isOrganizationObjective = (
  objective: ObjectiveDto,
  organizationId: string,
) => !objective.isPersonal && isInOrganizationArea(objective, organizationId);

// What GET /objectives/areas returns: "Jetzt" of every readable group/organization,
// with the personal goals others shared there
export const isAreaNowObjective = (
  objective: ObjectiveDto,
  userId: string | undefined,
) =>
  (!objective.isPersonal ||
    (objective.isShared && objective.ownerId !== userId)) &&
  objective.status === "ACTIVE" &&
  objective.horizon === "NOW";

// For components: filter in a useMemo, the store holds a map by id
export const objectivesWhere = (
  objectives: Record<string, ObjectiveDto>,
  belongs: (objective: ObjectiveDto) => boolean,
): ObjectiveDto[] =>
  Object.values(objectives)
    .filter((objective) => isVisible(objective) && belongs(objective))
    .sort((a, b) => a.position - b.position);

// A fetched list replaces its part of the store, so deleted objectives disappear
const replaceList = (
  state: objectivesState,
  belongs: (objective: ObjectiveDto) => boolean,
  objectives: ObjectiveDto[],
) => {
  for (const objective of Object.values(state.objectives)) {
    if (belongs(objective)) delete state.objectives[objective.id];
  }
  for (const objective of objectives) {
    state.objectives[objective.id] = objective;
  }
};

const upsert = (state: objectivesState, objective: ObjectiveDto) => {
  state.objectives[objective.id] = objective;
};

// Progress is calculated from the key results, like in the backend
const refreshProgress = (objective: ObjectiveDto) => {
  objective.keyResults.sort((a, b) => a.position - b.position);
  objective.progress = objectiveProgress(objective.keyResults);
};

export const objectivesSlice = createSlice({
  name: "objectives",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Lists
      .addCase(getPersonalObjectives.fulfilled, (state, action) => {
        const { userId, objectives } = action.payload;
        replaceList(
          state,
          (objective) => isPersonalObjective(objective, userId),
          objectives,
        );
      })
      .addCase(getAreaObjectives.fulfilled, (state, action) => {
        const { userId, objectives } = action.payload;
        replaceList(
          state,
          (objective) => isAreaNowObjective(objective, userId),
          objectives,
        );
      })
      .addCase(getGroupObjectives.fulfilled, (state, action) => {
        const { groupId, objectives } = action.payload;
        replaceList(
          state,
          (objective) => isInGroupArea(objective, groupId),
          objectives,
        );
      })
      .addCase(getOrganizationObjectives.fulfilled, (state, action) => {
        const { organizationId, objectives } = action.payload;
        replaceList(
          state,
          (objective) => isInOrganizationArea(objective, organizationId),
          objectives,
        );
      })

      // Single objectives (a check-in returns the updated objective too)
      .addCase(createPersonalObjective.fulfilled, (state, action) => {
        upsert(state, action.payload);
      })
      .addCase(createGroupObjective.fulfilled, (state, action) => {
        upsert(state, action.payload);
      })
      .addCase(createOrganizationObjective.fulfilled, (state, action) => {
        upsert(state, action.payload);
      })
      .addCase(getObjective.fulfilled, (state, action) => {
        upsert(state, action.payload);
      })
      .addCase(updateObjective.fulfilled, (state, action) => {
        upsert(state, action.payload);
      })
      .addCase(createCheckIn.fulfilled, (state, action) => {
        upsert(state, action.payload);
      })
      // Linking returns the updated objective too
      .addCase(linkProject.fulfilled, (state, action) => {
        upsert(state, action.payload);
      })
      .addCase(unlinkProject.fulfilled, (state, action) => {
        upsert(state, action.payload);
      })
      .addCase(linkTask.fulfilled, (state, action) => {
        upsert(state, action.payload);
      })
      .addCase(unlinkTask.fulfilled, (state, action) => {
        upsert(state, action.payload);
      })
      .addCase(deleteObjective.fulfilled, (state, action) => {
        const objectiveId = action.payload;
        delete state.objectives[objectiveId];
        // The backend unlinks child objectives (SetNull)
        for (const objective of Object.values(state.objectives)) {
          if (objective.parentObjective?.id === objectiveId) {
            objective.parentObjective = null;
          }
        }
      })

      // Key results
      .addCase(addKeyResult.fulfilled, (state, action) => {
        const objective = state.objectives[action.payload.objectiveId];
        if (!objective) return;
        objective.keyResults.push(action.payload.keyResult);
        refreshProgress(objective);
      })
      .addCase(updateKeyResult.fulfilled, (state, action) => {
        const objective = state.objectives[action.payload.objectiveId];
        if (!objective) return;
        const { keyResult } = action.payload;
        objective.keyResults = objective.keyResults.map((existing) =>
          existing.id === keyResult.id ? keyResult : existing,
        );
        refreshProgress(objective);
      })
      .addCase(deleteKeyResult.fulfilled, (state, action) => {
        const objective = state.objectives[action.payload.objectiveId];
        if (!objective) return;
        objective.keyResults = objective.keyResults.filter(
          (keyResult) => keyResult.id !== action.payload.keyResultId,
        );
        refreshProgress(objective);
      })

      // Loading and errors, the same for all thunks
      .addMatcher(isFulfilled(...allThunks), (state) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.isError = false;
      })
      .addMatcher(isPending(...allThunks), (state) => {
        state.isLoading = true;
        state.isSuccess = false;
      })
      .addMatcher(isRejected(...allThunks), (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload as string;
      });
  },
});

export default objectivesSlice.reducer;
